// peptideForm.ts
// Manages peptide input, list management, and session peptide loading
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { preparePeptide, checkPeptide, getSessionPeptides, deleteSessionPeptide, stopPeptide as stopPeptideAPI, launchDocking } from '../backendClient';
import { resolveCurrentSessionId, getSessionLoadGeneration, isSessionLoadCurrent } from '../sessionProjects';
import { prepareWorkflowListForItems, WORKFLOW_LIST_LOADING_HTML } from '../workflowListLoading';
// Global reference to update functions
let globalUpdatePeptideDetails = null;
let globalUpdatePeptideDeleteButtons = null;
// Global state to track deletion in progress
let isDeletingPeptide = false;
let peptideDeletionQueue = [];
let globalPeptides = new Map();
// Global reference to loadSessionPeptides function
let globalLoadSessionPeptides = null;
/**
 * Initializes the peptide management system with full backend integration.
 */
export function initPeptideForm() {
    // DOM elements
    const peptideInput = document.getElementById('peptide-input');
    const addPeptideBtn = document.getElementById('add-peptide');
    const peptideList = document.getElementById('peptide-list-container');
    const peptideDetails = document.getElementById('peptide-details');
    let peptideCount = 0;
    let nextLocalId = 1;
    const maxPeptides = 20;
    const peptides = globalPeptides; // Use global peptides map
    // Load existing session peptides on initialization
    loadSessionPeptides();
    // Auto-convert spaces to dashes in peptide input for easier typing
    // Users can still type dashes manually if they prefer
    peptideInput.addEventListener('input', (e) => {
        const target = e.target;
        const cursorPosition = target.selectionStart;
        const value = target.value;
        // Convert spaces to dashes
        const newValue = value.replace(/\s+/g, '-');
        // Only update if there was a change
        if (newValue !== value) {
            target.value = newValue;
            // Restore cursor position (adjust for length difference)
            const lengthDiff = newValue.length - value.length;
            const newCursorPosition = cursorPosition + lengthDiff;
            target.setSelectionRange(newCursorPosition, newCursorPosition);
        }
    });
    // React to docking updates to refresh delete buttons (peptides used by any docking are not deletable)
    window.addEventListener('dockings-updated', () => updatePeptideDeleteButtons());
    window.addEventListener('dockings-state', (e) => {
        const detail = (e === null || e === void 0 ? void 0 : e.detail) || {};
        const list = document.getElementById('peptide-list-container');
        if (!list)
            return;
        const items = Array.from(list.querySelectorAll('.peptide-item'));
        if (detail.anyProcessing) {
            items.forEach(it => {
                const del = it.querySelector('.btn-delete');
                if (del)
                    del.style.display = 'none';
            });
            return;
        }
        const referencedPeptides = new Set((detail.referencedPeptideIDs || []).filter((x) => typeof x === 'number'));
        items.forEach(it => {
            const del = it.querySelector('.btn-delete');
            if (!del)
                return;
            const localIdAttr = it.getAttribute('data-local-id');
            const peptideItem = localIdAttr ? peptides.get(parseInt(localIdAttr, 10)) : undefined;
            const backendId = peptideItem === null || peptideItem === void 0 ? void 0 : peptideItem.peptideID;
            if (typeof backendId === 'number' && referencedPeptides.has(backendId))
                del.style.display = 'none';
            else
                del.style.display = '';
        });
    });
    // Expose update functions globally
    globalUpdatePeptideDetails = updatePeptideDetails;
    globalUpdatePeptideDeleteButtons = updatePeptideDeleteButtons;
    globalLoadSessionPeptides = loadSessionPeptides;
    // Expose globally for dockingForm access
    window.updatePeptideDeleteButtons = updatePeptideDeleteButtons;
    // === Handle peptide addition ===
    addPeptideBtn.addEventListener('click', () => __awaiter(this, void 0, void 0, function* () {
        const value = (peptideInput.value || '').trim();
        if (!value || peptideCount >= maxPeptides)
            return;
        // Validate peptide sequence format
        if (!isValidPeptideSequence(value)) {
            alert('Please enter a peptide sequence (cannot be empty)');
            return;
        }
        // Check if peptide with same sequence already exists in current list (case-sensitive)
        // Normalize both sequences by converting spaces to dashes for comparison
        const normalizedValue = value.trim().replace(/\s+/g, '-');
        const existingPeptide = Array.from(peptides.values()).find(p => {
            const normalizedStored = p.peptideSequence.trim().replace(/\s+/g, '-');
            return normalizedStored === normalizedValue;
        });
        if (existingPeptide) {
            // Peptide already exists - just select it and clear input
            peptideInput.value = '';
            selectPeptide(existingPeptide);
            updatePeptideDetails();
            return;
        }
        // Launch peptide preparation FIRST, before creating UI element
        try {
            // Convert hyphens to spaces for backend (CPBuilder expects spaces, not hyphens)
            const sequenceForBackend = value.replace(/-/g, ' ');
            const response = yield preparePeptide(sequenceForBackend);
            if (!response.submitted || !response.peptideID || response.peptideID <= 0) {
                alert('Peptide preparation failed. Please try again.');
                peptideInput.value = '';
                return;
            }
            // Check if peptide with this ID already exists in the list (avoid duplicates)
            const existingPeptide = Array.from(peptides.values()).find(p => p.peptideID === response.peptideID);
            if (existingPeptide) {
                console.log(`[DEBUG] Peptide ${response.peptideID} already exists in UI, skipping duplicate creation`);
                peptideInput.value = '';
                // Update numbering for all peptides
                const allPeptideItems = Array.from(peptides.values()).sort((a, b) => (a.localID || 0) - (b.localID || 0));
                allPeptideItems.forEach((item, index) => {
                    if (item.element) {
                        const numberSpan = item.element.querySelector('.peptide-number');
                        if (numberSpan)
                            numberSpan.textContent = `${index + 1}`;
                    }
                });
                return;
            }
            // NOW create peptide item with valid peptideID
            const peptideItem = {
                localID: nextLocalId++,
                peptideID: response.peptideID,
                peptideSequence: value,
                peptideGenerationOK: false,
                status: 'pending'
            };
            // Create and render UI element with valid peptideID
            const element = createPeptideElement(peptideItem);
            peptideItem.element = element;
            peptides.set(peptideItem.localID, peptideItem);
            prepareWorkflowListForItems(peptideList);
            peptideList.appendChild(element);
            peptideInput.value = '';
            peptideCount++;
            // Update numbering for all peptides to ensure correct order
            const allPeptideItems = Array.from(peptides.values()).sort((a, b) => (a.localID || 0) - (b.localID || 0));
            allPeptideItems.forEach((item, index) => {
                if (item.element) {
                    const numberSpan = item.element.querySelector('.peptide-number');
                    if (numberSpan)
                        numberSpan.textContent = `${index + 1}`;
                }
            });
            updatePeptideDetails();
            updatePeptideDeleteButtons();
            updatePeptideInfoButtons();
            // Mark element as just launched to prevent "still in progress" toast on first refresh
            element.setAttribute('data-just-launched', 'true');
            // Show toast for first launch (success - green)
            const showToast = window.showToast;
            if (showToast) {
                showToast(`Peptide generation launched.`, 'success', 4000);
            }
            // Refresh peptide status to ensure containerId is stored and UI is up to date
            // This ensures the stop button is only shown when containerId is actually available
            yield refreshPeptide(peptideItem);
            // Remove the flag after first refresh
            element.removeAttribute('data-just-launched');
        }
        catch (error) {
            console.error('Error launching peptide preparation:', error);
            alert('Error launching peptide preparation. Please try again.');
            peptideInput.value = '';
        }
    }));
    // === Validate peptide sequence format ===
    function isValidPeptideSequence(sequence) {
        // Basic validation: sequence should not be empty and should contain some characters
        const trimmedSequence = sequence.trim();
        if (trimmedSequence.length === 0) {
            return false;
        }
        // Allow any characters - users can enter whatever they want
        // This includes non-canonical amino acids, modifications, etc.
        return true;
    }
    // === Format peptide sequence for display ===
    function formatPeptideSequence(sequence) {
        return sequence
            .replace(/\s+/g, '-')
            .replace(/-+/g, '-')
            .replace(/^-|-$/g, '')
            .replace(/NH2/g, 'NH₂'); // Convert NH2 to subscript for display
    }
    // === Create peptide UI element ===
    function createPeptideElement(peptideItem) {
        const wrapper = document.createElement('div');
        wrapper.className = 'peptide-item';
        wrapper.setAttribute('data-local-id', String(peptideItem.localID));
        if (peptideItem.peptideID) {
            wrapper.setAttribute('data-peptide-id', String(peptideItem.peptideID));
        }
        // Add click handler for peptide selection (only when ready)
        wrapper.addEventListener('click', (e) => {
            console.log('[DEBUG] Peptide element clicked:', peptideItem.localID);
            console.log('[DEBUG] Event target:', e.target);
            console.log('[DEBUG] Event currentTarget:', e.currentTarget);
            console.log('[DEBUG] peptideGenerationOK:', peptideItem.peptideGenerationOK);
            console.log('[DEBUG] peptideID:', peptideItem.peptideID);
            if (!peptideItem.peptideGenerationOK || !peptideItem.peptideID) {
                console.log('[DEBUG] Peptide not ready, skipping selection');
                return;
            }
            console.log('[DEBUG] Calling selectPeptide');
            selectPeptide(peptideItem);
        });
        // Header with number, status and controls
        const header = document.createElement('div');
        header.className = 'peptide-item-header';
        const leftSide = document.createElement('div');
        leftSide.style.display = 'flex';
        leftSide.style.alignItems = 'center';
        leftSide.style.gap = '8px';
        const numberSpan = document.createElement('span');
        numberSpan.className = 'peptide-number';
        numberSpan.textContent = '0';
        const statusSpan = document.createElement('span');
        // Use actual status from DynamoDB
        let statusLabel = '⏳ Processing...';
        let statusCls = 'processing';
        if (peptideItem.status === 'completed') {
            statusLabel = '✔ Ready';
            statusCls = 'ready';
        }
        else if (peptideItem.status === 'failed' || peptideItem.status === 'job_submission_failed') {
            statusLabel = '❌ Error';
            statusCls = 'error';
        }
        else if (peptideItem.status === 'in_progress') {
            statusLabel = '⏳ Processing...';
            statusCls = 'processing';
        }
        else if (peptideItem.status === 'pending') {
            statusLabel = '⏳ Pending...';
            statusCls = 'pending';
        }
        else if (peptideItem.status === 'cancelled') {
            statusLabel = '🚫 Cancelled';
            statusCls = 'error';
        }
        statusSpan.className = `peptide-status ${statusCls}`;
        statusSpan.textContent = statusLabel;
        leftSide.appendChild(numberSpan);
        leftSide.appendChild(statusSpan);
        const controls = document.createElement('div');
        controls.className = 'peptide-controls';
        // Manual check button (refresh status with backend)
        const checkBtn = document.createElement('button');
        checkBtn.className = 'btn-check btn-icon';
        checkBtn.innerHTML = '<svg class="check-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8"/><path d="M21 3v5h-5"/><path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16"/><path d="M3 21v-5h5"/></svg>';
        checkBtn.title = 'Refresh status';
        // Check button: only show during processing/pending
        if (peptideItem.status === 'pending' || peptideItem.status === 'in_progress') {
            checkBtn.classList.remove('btn-hidden');
            checkBtn.classList.add('btn-visible');
        }
        else {
            checkBtn.classList.remove('btn-visible');
            checkBtn.classList.add('btn-hidden');
        }
        checkBtn.addEventListener('click', () => {
            // Read peptideID from data attribute to ensure we have the latest value
            const peptideID = parseInt(wrapper.getAttribute('data-peptide-id') || '0', 10);
            if (peptideID > 0) {
                const peptideItemToCheck = peptides.get(peptideItem.localID);
                if (peptideItemToCheck) {
                    refreshPeptide(peptideItemToCheck);
                }
            }
            else {
                console.warn('[DEBUG] Cannot refresh peptide: invalid peptideID (0 or missing)');
            }
        });
        // Restart button (only show when cancelled)
        const restartBtn = document.createElement('button');
        restartBtn.className = 'btn-restart btn-icon';
        restartBtn.innerHTML = '<svg class="restart-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M1 4v6h6"/><path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10"/></svg>';
        restartBtn.title = 'Restart peptide generation';
        // Show restart button only when job is cancelled (like receptors)
        if (peptideItem.status === 'cancelled') {
            restartBtn.classList.remove('btn-hidden');
            restartBtn.classList.add('btn-visible');
        }
        else {
            restartBtn.classList.remove('btn-visible');
            restartBtn.classList.add('btn-hidden');
        }
        restartBtn.addEventListener('click', (e) => __awaiter(this, void 0, void 0, function* () {
            e.stopPropagation();
            // Read peptideID from data attribute to ensure we have the latest value
            const peptideID = parseInt(wrapper.getAttribute('data-peptide-id') || '0', 10);
            if (peptideID > 0 && peptideItem.peptideSequence) {
                yield restartPeptide(peptideID, peptideItem.peptideSequence, wrapper);
            }
            else {
                console.warn('[DEBUG] Cannot restart peptide: missing peptideID or sequence');
                const showToast = window.showToast;
                if (showToast) {
                    showToast('Cannot restart peptide: missing information. Please try again.', 'error', 4000);
                }
            }
        }));
        // Stop button (only show when processing/pending)
        const stopBtn = document.createElement('button');
        stopBtn.className = 'btn-stop btn-icon';
        stopBtn.innerHTML = '<svg class="stop-icon" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10" fill="currentColor"/><rect x="8" y="8" width="8" height="8" fill="white" rx="1"/></svg>';
        stopBtn.title = 'Stop peptide generation';
        // Show stop button only when job is in progress or pending
        if (peptideItem.status === 'in_progress' || peptideItem.status === 'pending') {
            stopBtn.classList.remove('btn-hidden');
            stopBtn.classList.add('btn-visible');
        }
        else {
            stopBtn.classList.remove('btn-visible');
            stopBtn.classList.add('btn-hidden');
        }
        stopBtn.addEventListener('click', (e) => __awaiter(this, void 0, void 0, function* () {
            e.stopPropagation();
            // Read peptideID from data attribute to ensure we have the latest value
            const peptideID = parseInt(wrapper.getAttribute('data-peptide-id') || '0', 10);
            if (peptideID > 0) {
                yield stopPeptide(peptideID);
            }
            else {
                console.warn('[DEBUG] Cannot stop peptide: invalid peptideID (0 or missing)');
                const showToast = window.showToast;
                if (showToast) {
                    showToast('Cannot stop peptide: peptide ID not yet available. Please wait a moment and try again.', 'warning', 4000);
                }
            }
        }));
        // Info button (hidden when peptide is not ready)
        const infoBtn = document.createElement('button');
        infoBtn.className = 'btn-info btn-icon';
        infoBtn.innerHTML = '<svg class="info-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4"/><path d="M12 8h.01"/></svg>';
        infoBtn.title = 'View peptide properties';
        // Hide info button if peptide is not ready or not completed
        if (!peptideItem.peptideGenerationOK || peptideItem.status !== 'completed') {
            infoBtn.classList.remove('btn-visible');
            infoBtn.classList.add('btn-hidden');
        }
        else {
            infoBtn.classList.remove('btn-hidden');
            infoBtn.classList.add('btn-visible');
        }
        infoBtn.addEventListener('click', (e) => {
            e.stopPropagation();
            if (peptideItem.peptideGenerationOK) {
                // Select the peptide item before showing the modal
                selectPeptide(peptideItem);
                showPeptideInfoModal(peptideItem);
            }
        });
        // Delete button
        const deleteBtn = document.createElement('button');
        deleteBtn.className = 'btn-delete btn-icon';
        deleteBtn.innerHTML = '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>';
        deleteBtn.title = 'Remove peptide';
        deleteBtn.addEventListener('click', (e) => __awaiter(this, void 0, void 0, function* () {
            e.stopPropagation();
            // Show confirmation modal
            const showConfirm = window.showConfirmationModal;
            if (!showConfirm) {
                console.error('showConfirmationModal not available');
                return;
            }
            const confirmed = yield showConfirm('Delete Peptide', `Are you sure you want to delete the peptide "${peptideItem.peptideSequence}"? This action cannot be undone.`, 'Delete', 'Cancel');
            if (!confirmed) {
                return;
            }
            // Add to deletion queue if not already queued
            if (!peptideDeletionQueue.includes(peptideItem.localID)) {
                peptideDeletionQueue.push(peptideItem.localID);
            }
            // Start processing queue if not already processing
            if (!isDeletingPeptide) {
                processPeptideDeletionQueue();
            }
        }));
        // Dock button (enabled when peptide is ready)
        const dockBtn = document.createElement('button');
        dockBtn.className = 'btn-dock';
        dockBtn.textContent = '⚓ Docking';
        dockBtn.disabled = !peptideItem.peptideGenerationOK;
        dockBtn.title = peptideItem.peptideGenerationOK ? 'Launch docking' : 'Peptide must be ready first';
        dockBtn.addEventListener('click', () => launchDockingForPeptide(peptideItem));
        controls.appendChild(checkBtn);
        controls.appendChild(restartBtn);
        controls.appendChild(stopBtn);
        controls.appendChild(infoBtn);
        controls.appendChild(deleteBtn);
        controls.appendChild(dockBtn);
        header.appendChild(leftSide);
        header.appendChild(controls);
        // Sequence on separate line
        const sequenceDiv = document.createElement('div');
        sequenceDiv.className = 'peptide-item-sequence';
        sequenceDiv.textContent = formatPeptideSequence(peptideItem.peptideSequence);
        wrapper.appendChild(header);
        wrapper.appendChild(sequenceDiv);
        // Initial delete visibility controlled by global rule
        updatePeptideDeleteButtons();
        updatePeptideElement(peptideItem);
        return wrapper;
    }
    // === Refresh peptide status ===
    function refreshPeptide(peptideItem) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a, _b, _c, _d, _e, _f, _g, _h;
            if (!peptideItem.peptideID) {
                console.error('Cannot check peptide status: no backend peptideID available');
                return;
            }
            try {
                console.log('[REFRESH PEPTIDE] Calling checkPeptide for peptideID:', peptideItem.peptideID);
                const response = yield checkPeptide(peptideItem.peptideID);
                console.log('[REFRESH PEPTIDE] Response from backend:', response);
                console.log('[REFRESH PEPTIDE] response.status =', response.status);
                if (peptideItem.element) {
                    // Update data-peptide-id attribute if peptideID is available
                    if (peptideItem.peptideID) {
                        peptideItem.element.setAttribute('data-peptide-id', String(peptideItem.peptideID));
                    }
                    const statusSpan = peptideItem.element.querySelector('.peptide-status');
                    const dockBtn = peptideItem.element.querySelector('.btn-dock');
                    const checkBtnRef = peptideItem.element.querySelector('.btn-check');
                    // Get previous status to detect changes
                    // Check if this is the first refresh after launch (element has data-just-launched attribute)
                    const isFirstRefresh = ((_a = peptideItem.element) === null || _a === void 0 ? void 0 : _a.hasAttribute('data-just-launched')) || false;
                    const previousStatus = peptideItem.status || 'pending';
                    if (response.status === 'completed' && response.peptidePath) {
                        peptideItem.peptideGenerationOK = true;
                        peptideItem.status = 'completed';
                        // Update peptide properties from response
                        if (response.peptideQED !== undefined)
                            peptideItem.peptideQED = response.peptideQED;
                        if (response.peptideALOGP !== undefined)
                            peptideItem.peptideALOGP = response.peptideALOGP;
                        if (response.peptideMW !== undefined)
                            peptideItem.peptideMW = response.peptideMW;
                        if (response.peptideHBD !== undefined)
                            peptideItem.peptideHBD = response.peptideHBD;
                        if (response.peptideHBA !== undefined)
                            peptideItem.peptideHBA = response.peptideHBA;
                        if (response.peptidePSA !== undefined)
                            peptideItem.peptidePSA = response.peptidePSA;
                        if (response.peptideROTB !== undefined)
                            peptideItem.peptideROTB = response.peptideROTB;
                        if (response.peptideAROM !== undefined)
                            peptideItem.peptideAROM = response.peptideAROM;
                        // Update the entire element
                        updatePeptideElement(peptideItem);
                        // Update delete buttons when peptide becomes ready
                        updatePeptideDeleteButtons();
                        updatePeptideInfoButtons();
                        // Auto-select the peptide when it becomes completed
                        selectPeptide(peptideItem);
                        // Load peptide in Mol* viewer when ready
                        if (response.peptidePath) {
                            (_b = window.loadPdbInMolstar) === null || _b === void 0 ? void 0 : _b.call(window, `peptide-${peptideItem.peptideID}`, response.peptidePath);
                        }
                        // Show toast if status changed to completed (but not during stop)
                        if (previousStatus !== 'completed' && !((_c = peptideItem.element) === null || _c === void 0 ? void 0 : _c.hasAttribute('data-stopping'))) {
                            const showToast = window.showToast;
                            if (showToast) {
                                // Only show toast if not during bulk refresh
                                if (!((_d = peptideItem.element) === null || _d === void 0 ? void 0 : _d.hasAttribute('data-refreshing-all'))) {
                                    showToast('Peptide generation is now ready.', 'success', 4000);
                                }
                            }
                        }
                    }
                    else if (response.status && response.status === 'cancelled') {
                        peptideItem.peptideGenerationOK = false;
                        peptideItem.status = 'cancelled';
                        // Update status immediately in UI (like receptor)
                        if (statusSpan) {
                            statusSpan.textContent = '🚫 Cancelled';
                            statusSpan.className = 'peptide-status error';
                        }
                        // Hide check and stop buttons when cancelled, show restart button
                        if (checkBtnRef) {
                            checkBtnRef.classList.remove('btn-visible');
                            checkBtnRef.classList.add('btn-hidden');
                        }
                        const stopBtnRef = peptideItem.element.querySelector('.btn-stop');
                        if (stopBtnRef) {
                            stopBtnRef.classList.remove('btn-visible');
                            stopBtnRef.classList.add('btn-hidden');
                        }
                        const restartBtnRef = peptideItem.element.querySelector('.btn-restart');
                        if (restartBtnRef) {
                            restartBtnRef.classList.remove('btn-hidden');
                            restartBtnRef.classList.add('btn-visible');
                        }
                        // Update the entire element
                        updatePeptideElement(peptideItem);
                        updatePeptideDeleteButtons();
                        updatePeptideInfoButtons();
                    }
                    else if (response.status === 'failed' || response.status === 'job_submission_failed') {
                        peptideItem.peptideGenerationOK = false;
                        peptideItem.status = 'failed';
                        // Hide check, stop and restart buttons when failed
                        if (checkBtnRef) {
                            checkBtnRef.classList.remove('btn-visible');
                            checkBtnRef.classList.add('btn-hidden');
                        }
                        const stopBtnRef = peptideItem.element.querySelector('.btn-stop');
                        if (stopBtnRef) {
                            stopBtnRef.classList.remove('btn-visible');
                            stopBtnRef.classList.add('btn-hidden');
                        }
                        const restartBtnRef = peptideItem.element.querySelector('.btn-restart');
                        if (restartBtnRef) {
                            restartBtnRef.classList.remove('btn-visible');
                            restartBtnRef.classList.add('btn-hidden');
                        }
                        // Update the entire element
                        updatePeptideElement(peptideItem);
                        updatePeptideDeleteButtons();
                        updatePeptideInfoButtons();
                        // Show toast if status changed to failed (but not during stop)
                        if (previousStatus !== 'failed' && previousStatus !== 'job_submission_failed' && !((_e = peptideItem.element) === null || _e === void 0 ? void 0 : _e.hasAttribute('data-stopping'))) {
                            const showToast = window.showToast;
                            if (showToast) {
                                // Only show toast if not during bulk refresh
                                if (!((_f = peptideItem.element) === null || _f === void 0 ? void 0 : _f.hasAttribute('data-refreshing-all'))) {
                                    showToast('Peptide generation has failed.', 'error', 4000);
                                }
                            }
                        }
                    }
                    else {
                        peptideItem.peptideGenerationOK = false;
                        peptideItem.status = response.status || 'pending';
                        // Update the entire element
                        updatePeptideElement(peptideItem);
                        updatePeptideDeleteButtons();
                        updatePeptideInfoButtons();
                        // Show toast if still processing (but not on first refresh after launch or stop, or during bulk refresh)
                        const currentStatus = response.status;
                        if ((currentStatus === 'pending' || currentStatus === 'in_progress' || currentStatus === 'processing') && !isFirstRefresh && !((_g = peptideItem.element) === null || _g === void 0 ? void 0 : _g.hasAttribute('data-stopping')) && !((_h = peptideItem.element) === null || _h === void 0 ? void 0 : _h.hasAttribute('data-refreshing-all'))) {
                            const showToast = window.showToast;
                            if (showToast) {
                                showToast('Peptide generation is still in progress.', 'info', 4000);
                            }
                        }
                    }
                }
                updatePeptideDetails();
            }
            catch (error) {
                console.error('Error checking peptide status:', error);
                if (peptideItem.element) {
                    const statusSpan = peptideItem.element.querySelector('.peptide-status');
                    if (statusSpan) {
                        statusSpan.textContent = '❌ Error';
                        statusSpan.className = 'peptide-status error';
                    }
                }
            }
        });
    }
    /**
     * Restarts a cancelled peptide generation by launching a new job with the same sequence
     * @param peptideID - The current peptide ID (will be replaced with new one)
     * @param peptideSequence - The peptide sequence to restart
     * @param element - The DOM element to update
     */
    function restartPeptide(peptideID, peptideSequence, element) {
        return __awaiter(this, void 0, void 0, function* () {
            // Hide restart button immediately and update UI (like receptors)
            const restartBtn = element.querySelector('.btn-restart');
            if (restartBtn) {
                restartBtn.classList.remove('btn-visible');
                restartBtn.classList.add('btn-hidden');
            }
            // Show refresh and stop buttons immediately
            const refreshBtn = element.querySelector('.btn-check');
            if (refreshBtn) {
                refreshBtn.classList.remove('btn-hidden');
                refreshBtn.classList.add('btn-visible');
            }
            const stopBtn = element.querySelector('.btn-stop');
            if (stopBtn) {
                stopBtn.classList.remove('btn-hidden');
                stopBtn.classList.add('btn-visible');
            }
            // Update status immediately
            const statusSpan = element.querySelector('.peptide-status');
            if (statusSpan) {
                statusSpan.textContent = '⏳ Pending...';
                statusSpan.className = 'peptide-status pending';
            }
            // Mark element as just restarted to prevent "still in progress" toast on first refresh
            element.setAttribute('data-just-launched', 'true');
            // Hide delete button immediately (item is now processing again)
            updatePeptideDeleteButtons();
            const showToast = window.showToast;
            try {
                // Restart the peptide generation with the SAME peptideID (don't create a new one)
                // This will update the existing entry in DB with status pending/in_progress
                const result = yield preparePeptide(peptideSequence);
                if (result.submitted && result.peptideID && result.peptideID > 0) {
                    // Verify the peptideID matches (should be the same for restart)
                    if (result.peptideID !== peptideID) {
                        console.warn(`[RESTART PEPTIDE] Expected peptideID ${peptideID} but got ${result.peptideID}`);
                    }
                    // Update the element with the peptide ID (should be the same)
                    element.setAttribute('data-peptide-id', String(result.peptideID));
                    // Update the peptide item in our global map
                    const localID = parseInt(element.getAttribute('data-local-id') || '0', 10);
                    const peptideItem = globalPeptides.get(localID);
                    if (peptideItem) {
                        peptideItem.peptideID = result.peptideID;
                        peptideItem.status = 'pending';
                        peptideItem.peptideGenerationOK = false;
                    }
                    // Mark as restarting to prevent toasts from refreshPeptide
                    element.setAttribute('data-restarting', 'true');
                    // Refresh the peptide status to update UI
                    if (peptideItem) {
                        yield refreshPeptide(peptideItem);
                    }
                    // Remove flags after refresh
                    element.removeAttribute('data-just-launched');
                    element.removeAttribute('data-restarting');
                    // Show success toast (only one toast, replaces any previous)
                    if (showToast) {
                        showToast(`Peptide generation restarted for sequence ${peptideSequence}.`, 'success', 4000);
                    }
                }
                else {
                    element.removeAttribute('data-just-launched');
                    if (showToast) {
                        showToast('Failed to restart peptide generation. Please try again.', 'error', 4000);
                    }
                }
            }
            catch (error) {
                console.error('Error restarting peptide:', error);
                element.removeAttribute('data-just-launched');
                if (showToast) {
                    showToast('Error restarting peptide generation. Please try again.', 'error', 4000);
                }
            }
        });
    }
    function stopPeptide(peptideID) {
        return __awaiter(this, void 0, void 0, function* () {
            // Find the peptide item first (declare outside try so it's available in catch)
            const peptideItem = Array.from(globalPeptides.values()).find(p => p.peptideID === peptideID);
            if (!peptideItem) {
                alert('Peptide not found.');
                return;
            }
            try {
                // Show confirmation modal IMMEDIATELY (like receptor)
                const showConfirm = window.showConfirmationModal;
                if (!showConfirm) {
                    alert('Confirmation modal not available. Please refresh the page.');
                    return;
                }
                const confirmed = yield showConfirm('Stop Peptide Generation', 'Are you sure you want to stop this peptide generation? The process will be stopped and cannot be resumed.', 'Stop', 'Cancel');
                if (!confirmed) {
                    return;
                }
                // Send toast IMMEDIATELY after confirmation (like receptor)
                const showToast = window.showToast;
                if (showToast) {
                    showToast('Peptide generation will be stopped.', 'info', 4000);
                }
                // Mark element as stopping (prevents toasts from refreshPeptide)
                if (peptideItem.element) {
                    peptideItem.element.setAttribute('data-stopping', 'true');
                }
                // Check status from backend FIRST to see if job is already done (before calling stop API)
                yield refreshPeptide(peptideItem);
                // Check if the job is already completed, failed, or cancelled
                if (peptideItem.status === 'completed') {
                    // Remove flag and show our toast
                    if (peptideItem.element)
                        peptideItem.element.removeAttribute('data-stopping');
                    if (showToast) {
                        showToast('This peptide generation has already completed successfully.', 'success', 4000);
                    }
                    return;
                }
                if (peptideItem.status === 'failed' || peptideItem.status === 'job_submission_failed') {
                    // Remove flag and show our toast
                    if (peptideItem.element)
                        peptideItem.element.removeAttribute('data-stopping');
                    if (showToast) {
                        showToast('This peptide generation has already failed.', 'error', 4000);
                    }
                    return;
                }
                if (peptideItem.status === 'cancelled') {
                    // Remove flag and show our toast
                    if (peptideItem.element)
                        peptideItem.element.removeAttribute('data-stopping');
                    if (showToast) {
                        showToast('This peptide generation has already been cancelled.', 'warning', 4000);
                    }
                    return;
                }
                // If still in progress, proceed with stop
                const result = yield stopPeptideAPI(peptideID);
                if (result.success) {
                    console.log('[STOP PEPTIDE] Backend returned success, now checking status...');
                    // Refresh the peptide status to update UI (but no toast from refreshPeptide due to data-stopping)
                    yield refreshPeptide(peptideItem);
                    console.log('[STOP PEPTIDE] After refreshPeptide, peptideItem.status =', peptideItem.status);
                    // Remove flag and show our toast
                    if (peptideItem.element)
                        peptideItem.element.removeAttribute('data-stopping');
                    if (showToast) {
                        showToast('Peptide generation has been cancelled successfully.', 'success', 4000);
                    }
                }
                else {
                    // Refresh status to get the actual current state (but no toast from checkPeptideStatus due to data-stopping)
                    yield refreshPeptide(peptideItem);
                    // Remove flag and show our toast
                    if (peptideItem.element)
                        peptideItem.element.removeAttribute('data-stopping');
                    if (showToast) {
                        showToast(result.message || 'Failed to stop peptide generation. Please try again.', 'error', 4000);
                    }
                    else {
                        alert(result.message || 'Failed to stop peptide generation. Please try again.');
                    }
                }
            }
            catch (error) {
                console.error('Error stopping peptide:', error);
                // Remove flag in case of error
                if (peptideItem.element) {
                    peptideItem.element.removeAttribute('data-stopping');
                }
                const showToast = window.showToast;
                if (showToast) {
                    showToast('An error occurred while stopping the peptide generation. Please try again.', 'error', 4000);
                }
                else {
                    alert('An error occurred while stopping the peptide generation. Please try again.');
                }
            }
        });
    }
    function updatePeptideDeleteButtons() {
        const list = document.getElementById('peptide-list-container');
        if (!list)
            return;
        const items = Array.from(list.querySelectorAll('.peptide-item'));
        // Check if any peptide is processing
        const anyProcessing = items.some(it => {
            const st = it.querySelector('.peptide-status');
            return st && (st.classList.contains('processing') || st.classList.contains('pending'));
        });
        // If we're currently deleting, hide ALL delete buttons
        if (isDeletingPeptide) {
            items.forEach(it => {
                const del = it.querySelector('.btn-delete');
                if (del) {
                    del.classList.add('hidden');
                    del.disabled = true;
                }
            });
            return;
        }
        // Check docking state
        import('../backendClient').then(({ getSessionDockings }) => getSessionDockings()).then((d) => {
            const dockings = (d === null || d === void 0 ? void 0 : d.dockings) || [];
            const anyDockingProcessing = dockings.some((dk) => dk && (dk.status === 'pending' || dk.status === 'in_progress'));
            if (anyDockingProcessing) {
                // When any docking is processing, hide delete buttons for peptides involved in dockings
                // BUT allow deletion of other peptides
                const referencedPeptides = new Set();
                dockings.forEach((dk) => {
                    if (dk && typeof dk.peptideID === 'number')
                        referencedPeptides.add(dk.peptideID);
                });
                items.forEach(it => {
                    var _a, _b, _c, _d;
                    const del = it.querySelector('.btn-delete');
                    if (!del)
                        return;
                    const localIdAttr = it.getAttribute('data-local-id');
                    const peptideItem = localIdAttr ? peptides.get(parseInt(localIdAttr, 10)) : undefined;
                    const backendId = peptideItem === null || peptideItem === void 0 ? void 0 : peptideItem.peptideID;
                    const isReferenced = typeof backendId === 'number' && referencedPeptides.has(backendId);
                    const isProcessing = ((_a = it.querySelector('.peptide-status')) === null || _a === void 0 ? void 0 : _a.classList.contains('processing')) ||
                        ((_b = it.querySelector('.peptide-status')) === null || _b === void 0 ? void 0 : _b.classList.contains('pending'));
                    // Hide delete button if: peptide is processing (but allow cancelled) OR peptide is involved in docking
                    if ((isProcessing && !((_d = (_c = it.querySelector('.peptide-status')) === null || _c === void 0 ? void 0 : _c.textContent) === null || _d === void 0 ? void 0 : _d.includes('Cancelled'))) || isReferenced) {
                        del.classList.add('hidden');
                        del.disabled = true;
                    }
                    else {
                        del.classList.remove('hidden');
                        del.disabled = false;
                    }
                });
            }
            else {
                // No docking processing - hide delete for peptides involved in any docking (any status)
                const referencedPeptides = new Set();
                dockings.forEach((dk) => {
                    if (dk && typeof dk.peptideID === 'number')
                        referencedPeptides.add(dk.peptideID);
                });
                items.forEach(it => {
                    var _a, _b, _c, _d;
                    const del = it.querySelector('.btn-delete');
                    if (!del)
                        return;
                    const localIdAttr = it.getAttribute('data-local-id');
                    const peptideItem = localIdAttr ? peptides.get(parseInt(localIdAttr, 10)) : undefined;
                    const backendId = peptideItem === null || peptideItem === void 0 ? void 0 : peptideItem.peptideID;
                    const isReferenced = typeof backendId === 'number' && referencedPeptides.has(backendId);
                    const isProcessing = ((_a = it.querySelector('.peptide-status')) === null || _a === void 0 ? void 0 : _a.classList.contains('processing')) ||
                        ((_b = it.querySelector('.peptide-status')) === null || _b === void 0 ? void 0 : _b.classList.contains('pending'));
                    // Hide delete button if: peptide is processing (but allow cancelled) OR peptide is involved in docking
                    if ((isProcessing && !((_d = (_c = it.querySelector('.peptide-status')) === null || _c === void 0 ? void 0 : _c.textContent) === null || _d === void 0 ? void 0 : _d.includes('Cancelled'))) || isReferenced) {
                        del.classList.add('hidden');
                        del.disabled = true;
                    }
                    else {
                        del.classList.remove('hidden');
                        del.disabled = false;
                    }
                });
            }
        }).catch(() => {
            // Fallback: show deletes when docking fetch failed
            // BUT only if no peptides are processing
            if (!anyProcessing) {
                items.forEach(it => {
                    const del = it.querySelector('.btn-delete');
                    if (del) {
                        del.classList.remove('hidden');
                        del.disabled = false;
                    }
                });
            }
        });
    }
    function updatePeptideInfoButtons() {
        const list = document.getElementById('peptide-list-container');
        if (!list)
            return;
        const items = Array.from(list.querySelectorAll('.peptide-item'));
        items.forEach(it => {
            const infoBtn = it.querySelector('.btn-info');
            if (!infoBtn)
                return;
            const localIdAttr = it.getAttribute('data-local-id');
            if (localIdAttr) {
                const localId = parseInt(localIdAttr);
                const peptideItem = peptides.get(localId);
                if (peptideItem) {
                    if (peptideItem.peptideGenerationOK && peptideItem.status === 'completed') {
                        infoBtn.classList.remove('btn-hidden');
                        infoBtn.classList.add('btn-visible');
                    }
                    else {
                        infoBtn.classList.remove('btn-visible');
                        infoBtn.classList.add('btn-hidden');
                    }
                }
            }
        });
    }
    function updatePeptideElement(peptideItem) {
        if (!peptideItem.element)
            return;
        // Update status display
        const statusSpan = peptideItem.element.querySelector('.peptide-status');
        if (statusSpan) {
            let statusLabel = '⏳ Processing...';
            let statusCls = 'processing';
            if (peptideItem.status === 'completed') {
                statusLabel = '✔ Ready';
                statusCls = 'ready';
            }
            else if (peptideItem.status === 'failed' || peptideItem.status === 'job_submission_failed') {
                statusLabel = '❌ Error';
                statusCls = 'error';
            }
            else if (peptideItem.status === 'in_progress') {
                statusLabel = '⏳ Processing...';
                statusCls = 'processing';
            }
            else if (peptideItem.status === 'pending') {
                statusLabel = '⏳ Pending...';
                statusCls = 'pending';
            }
            else if (peptideItem.status === 'cancelled') {
                statusLabel = '🚫 Cancelled';
                statusCls = 'error';
            }
            statusSpan.className = `peptide-status ${statusCls}`;
            statusSpan.textContent = statusLabel;
        }
        // Update buttons visibility and state by status - use display:none to avoid empty spaces
        const checkBtn = peptideItem.element.querySelector('.btn-check');
        const stopBtn = peptideItem.element.querySelector('.btn-stop');
        const infoBtn = peptideItem.element.querySelector('.btn-info');
        const dockBtn = peptideItem.element.querySelector('.btn-dock');
        if (checkBtn) {
            // Check button: only show during processing/pending
            if (peptideItem.status === 'pending' || peptideItem.status === 'in_progress') {
                checkBtn.classList.remove('btn-hidden');
                checkBtn.classList.add('btn-visible');
            }
            else {
                checkBtn.classList.remove('btn-visible');
                checkBtn.classList.add('btn-hidden');
            }
        }
        // Restart button: only show when cancelled
        const restartBtn = peptideItem.element.querySelector('.btn-restart');
        if (restartBtn) {
            if (peptideItem.status === 'cancelled') {
                restartBtn.classList.remove('btn-hidden');
                restartBtn.classList.add('btn-visible');
            }
            else {
                restartBtn.classList.remove('btn-visible');
                restartBtn.classList.add('btn-hidden');
            }
        }
        if (stopBtn) {
            // Stop button: only show during processing/pending
            if (peptideItem.status === 'in_progress' || peptideItem.status === 'pending') {
                stopBtn.classList.remove('btn-hidden');
                stopBtn.classList.add('btn-visible');
            }
            else {
                stopBtn.classList.remove('btn-visible');
                stopBtn.classList.add('btn-hidden');
            }
        }
        if (infoBtn) {
            // Info button: only show when completed
            if (peptideItem.peptideGenerationOK && peptideItem.status === 'completed') {
                infoBtn.classList.remove('btn-hidden');
                infoBtn.classList.add('btn-visible');
            }
            else {
                infoBtn.classList.remove('btn-visible');
                infoBtn.classList.add('btn-hidden');
            }
        }
        if (dockBtn) {
            // Dock button: always visible but enabled only when ready
            dockBtn.disabled = !peptideItem.peptideGenerationOK;
            dockBtn.title = peptideItem.peptideGenerationOK ? 'Launch docking' : 'Peptide must be ready first';
        }
    }
    // === Delete peptide ===
    function deletePeptide(localID) {
        return __awaiter(this, void 0, void 0, function* () {
            const peptideItem = peptides.get(localID);
            if (!peptideItem)
                return;
            try {
                // Delete from backend (if peptideID available)
                if (peptideItem.peptideID) {
                    const res = yield deleteSessionPeptide(peptideItem.peptideID);
                    if (!res.deleted) {
                        console.error(`Failed to delete peptide ${peptideItem.peptideID} from backend`, res);
                        if (res.reason === 'blocked_by_docking') {
                            alert('Cannot delete this peptide because a docking references it. Remove related dockings first.');
                        }
                        else {
                            alert(res.message || 'Failed to delete peptide from database. Please try again.');
                        }
                        // Reset deletion state on failure
                        isDeletingPeptide = false;
                        updatePeptideDeleteButtons();
                        return; // Don't remove from UI if backend deletion failed
                    }
                }
                // Remove from UI immediately after successful backend deletion
                if (peptideItem.element)
                    peptideItem.element.remove();
                const deletedBackendId = peptideItem.peptideID;
                peptides.delete(localID);
                peptideCount--;
                // Clear global selection if this was the selected peptide
                if (typeof deletedBackendId === 'number' && window.currentPeptideID === deletedBackendId) {
                    window.currentPeptideID = undefined;
                    // Remove peptide from Mol* visualizer
                    if (window.removeFromMolstar) {
                        window.removeFromMolstar(`peptide-${deletedBackendId}`);
                    }
                }
                // Update UI state
                updatePeptideDetails();
                // Reset deletion state and update delete buttons
                isDeletingPeptide = false;
                updatePeptideDeleteButtons();
                updatePeptideInfoButtons();
            }
            catch (error) {
                console.error('Error deleting peptide from session:', error);
                alert('Error deleting peptide. Please try again.');
                // Reset deletion state on error
                isDeletingPeptide = false;
                updatePeptideDeleteButtons();
            }
        });
    }
    // No renumbering needed - items are sorted by creation date from backend
    // === Load session peptides ===
    function loadSessionPeptides() {
        return __awaiter(this, void 0, void 0, function* () {
            const requestedSessionId = resolveCurrentSessionId();
            const loadGen = getSessionLoadGeneration();
            try {
                if (!peptideList.querySelector('.workflow-list-loading') && !peptideList.querySelector('.peptide-item')) {
                    peptideList.innerHTML = WORKFLOW_LIST_LOADING_HTML;
                }
                const sessionData = yield getSessionPeptides();
                if (!isSessionLoadCurrent(loadGen, requestedSessionId))
                    return;
                if (sessionData && sessionData.peptides && sessionData.peptides.length > 0) {
                    // Clear existing peptides first to avoid duplicates
                    peptides.clear();
                    peptideList.innerHTML = '';
                    peptideCount = 0;
                    nextLocalId = 1;
                    for (const peptideData of sessionData.peptides) {
                        const peptideItem = {
                            peptideID: peptideData.peptideID, // Backend peptideID
                            localID: nextLocalId++, // Local numbering for UI
                            peptideSequence: peptideData.peptideSequence,
                            peptideGenerationOK: peptideData.peptideGenerationOK || false,
                            status: peptideData.status || 'pending',
                            statusMessage: peptideData.statusMessage || '',
                            // Database properties
                            peptideQED: peptideData.peptideQED,
                            peptideALOGP: peptideData.peptideALOGP,
                            peptideMW: peptideData.peptideMW,
                            peptideHBD: peptideData.peptideHBD,
                            peptideHBA: peptideData.peptideHBA,
                            peptidePSA: peptideData.peptidePSA,
                            peptideROTB: peptideData.peptideROTB,
                            peptideAROM: peptideData.peptideAROM
                        };
                        const element = createPeptideElement(peptideItem);
                        peptideItem.element = element;
                        peptides.set(peptideItem.localID, peptideItem);
                        peptideList.appendChild(element);
                        peptideCount++;
                        // Update number label - use chronological order (1, 2, 3...)
                        const numberSpan = element.querySelector('.peptide-number');
                        if (numberSpan)
                            numberSpan.textContent = `${peptideCount}`;
                        // Update status based on DynamoDB data
                        if (peptideItem.element) {
                            const statusSpan = peptideItem.element.querySelector('.peptide-status');
                            const dockBtn = peptideItem.element.querySelector('.btn-dock');
                            const checkBtnRef = peptideItem.element.querySelector('.btn-check');
                            // Use actual status from DynamoDB
                            let statusLabel = '⏳ Processing...';
                            let statusCls = 'processing';
                            if (peptideItem.status === 'completed') {
                                statusLabel = '✔ Ready';
                                statusCls = 'ready';
                            }
                            else if (peptideItem.status === 'failed' || peptideItem.status === 'job_submission_failed') {
                                statusLabel = '❌ Error';
                                statusCls = 'error';
                            }
                            else if (peptideItem.status === 'in_progress') {
                                statusLabel = '⏳ Processing...';
                                statusCls = 'processing';
                            }
                            if (statusSpan) {
                                statusSpan.textContent = statusLabel;
                                statusSpan.className = `peptide-status ${statusCls}`;
                            }
                            // Ensure peptideGenerationOK is set for completed peptides
                            if (peptideItem.status === 'completed') {
                                peptideItem.peptideGenerationOK = true;
                            }
                            // Update element with correct button states
                            updatePeptideElement(peptideItem);
                        }
                    }
                    updatePeptideDetails();
                    updatePeptideDeleteButtons();
                    updatePeptideInfoButtons();
                }
                else if (isSessionLoadCurrent(loadGen, requestedSessionId)) {
                    // Empty session: clear leftover rows from a previous project.
                    peptides.clear();
                    peptideList.innerHTML = '';
                    peptideCount = 0;
                    nextLocalId = 1;
                    updatePeptideDetails();
                    updatePeptideDeleteButtons();
                    updatePeptideInfoButtons();
                }
            }
            catch (error) {
                console.error('Error loading session peptides:', error);
            }
        });
    }
    // No JS height sync; CSS variable manages list height consistently like other lists
    // === Select peptide for visualization ===
    function selectPeptide(peptideItem) {
        var _a, _b, _c;
        console.log('[DEBUG] selectPeptide called for:', peptideItem.localID);
        console.log('[DEBUG] peptideItem.element exists:', !!peptideItem.element);
        // Always reselect for consistency with docking list
        // Remove selection from all other peptides
        const allPeptides = peptideList.querySelectorAll('.peptide-item');
        allPeptides.forEach(item => item.classList.remove('selected'));
        // Select this peptide
        if (peptideItem.element) {
            peptideItem.element.classList.add('selected');
            console.log(`[DEBUG] Peptide element selected: ${peptideItem.localID}`);
        }
        else {
            console.error('[DEBUG] No element found for peptide:', peptideItem.localID);
        }
        // Load peptide in Mol* if it's ready
        if (peptideItem.peptideGenerationOK && peptideItem.peptideID) {
            // Track globally which peptide is selected so tab switches can restore view
            window.currentPeptideID = peptideItem.peptideID;
            // Construct the path to the peptide PDB file
            // Get current user from localStorage
            const currentUser = localStorage.getItem('currentUser');
            if (currentUser) {
                const peptidePath = `/frontend/userSessions/dataBase/peptide-${String(peptideItem.peptideID).padStart(5, '0')}/reprPeptide.pdb`;
                console.log(`🎯 [DEBUG] Loading selected peptide in Mol*: ${peptidePath}`);
                (_a = window.loadPdbInMolstar) === null || _a === void 0 ? void 0 : _a.call(window, `peptide-${peptideItem.peptideID}`, peptidePath);
            }
        }
        else {
            console.log(`[DEBUG] Peptide ${peptideItem.localID} not ready for visualization`);
        }
        // Update screening launch text if screening view exists (receptor tab)
        (_c = (_b = window).updateScreeningLaunchText) === null || _c === void 0 ? void 0 : _c.call(_b);
    }
    // === Launch docking for peptide ===
    function launchDockingForPeptide(peptideItem) {
        return __awaiter(this, void 0, void 0, function* () {
            // Check if either predefined or custom binding site is selected
            const hasBindingSite = window.currentBindingSiteID;
            const hasCustomBindingSite = window.currentCustomBindingSiteID;
            if (!peptideItem.peptideGenerationOK || (!hasBindingSite && !hasCustomBindingSite) || !peptideItem.peptideID || !window.currentReceptorID) {
                alert('Peptide must be ready, receptor and binding site must be selected');
                return;
            }
            try {
                // Determine binding site ID and type
                const bindingSiteID = hasCustomBindingSite ? -1 : window.currentBindingSiteID;
                const customBindingSite = hasCustomBindingSite ? window.currentCustomBindingSiteID : undefined;
                console.log(`[launchDockingForPeptide] Binding site info:`, {
                    hasCustomBindingSite,
                    currentCustomBindingSiteID: window.currentCustomBindingSiteID,
                    bindingSiteID,
                    customBindingSite
                });
                // Check if docking already exists first
                const { getDockingList } = yield import('./dockingForm');
                const dockingList = getDockingList();
                console.log(`🔍 [DEBUG] Checking for existing docking:`, {
                    currentReceptorID: window.currentReceptorID,
                    peptideID: peptideItem.peptideID,
                    bindingSiteID,
                    customBindingSite,
                    existingDockings: dockingList.map(d => ({ receptorID: d.receptorID, peptideID: d.peptideID, bindingSiteID: d.bindingSiteID, status: d.status }))
                });
                // Find existing docking (compare bindingSiteID to handle both predefined and custom)
                const existingDocking = dockingList.find(d => d.receptorID === window.currentReceptorID &&
                    d.peptideID === peptideItem.peptideID &&
                    d.bindingSiteID === bindingSiteID // -1 for custom, positive number for predefined
                );
                if (existingDocking && (existingDocking.status === 'in_progress' || existingDocking.status === 'completed')) {
                    // Existing docking in progress/completed - just select it
                    console.log(`🔍 [DEBUG] Existing docking found (${existingDocking.status}), selecting it`);
                    const { selectDocking } = yield import('./dockingForm');
                    selectDocking(existingDocking);
                    // Switch to Docking Results tab
                    switchToDockingTab();
                }
                else {
                    // New docking or failed docking - launch backend first, then add to form
                    console.log(`🔍 [DEBUG] Launching new docking in backend (custom: ${!!customBindingSite})`);
                    yield launchDocking(bindingSiteID, window.currentReceptorID, peptideItem.peptideID, customBindingSite);
                    console.log(`Docking launched for peptide ${peptideItem.localID} (backend ID: ${peptideItem.peptideID})`);
                    // Add docking to the docking form after backend launch
                    const { addDockingToForm } = yield import('./dockingForm');
                    const newDockingID = addDockingToForm(window.currentReceptorID, peptideItem.peptideID, bindingSiteID, window.currentPdbID || '', peptideItem.peptideSequence, 'pending', // status
                    undefined, // backendDockingID
                    undefined, // poses
                    customBindingSite // customBindingSiteId for display
                    );
                    // Switch to Docking Results tab and select the new docking
                    switchToDockingTab();
                    // Find and select the newly created docking
                    setTimeout(() => __awaiter(this, void 0, void 0, function* () {
                        const { getDockingList, selectDocking } = yield import('./dockingForm');
                        const dockingList = getDockingList();
                        const newDocking = dockingList.find(d => d.dockingID === newDockingID);
                        if (newDocking) {
                            selectDocking(newDocking);
                        }
                    }), 100); // Small delay to ensure the docking is added to the DOM
                }
            }
            catch (error) {
                console.error('Error launching docking:', error);
                alert('Error launching docking. Please try again.');
            }
        });
    }
    // === Switch to Docking Results tab ===
    function switchToDockingTab() {
        const dockingTabButton = document.getElementById('docking-tab-button');
        if (dockingTabButton) {
            console.log('[DEBUG] Switching to docking tab');
            dockingTabButton.click();
        }
        else {
            console.error('[DEBUG] Docking tab button not found');
        }
    }
    // === Update peptide details text ===
    function updatePeptideDetails() {
        // Only show messages when there are peptides in the list
        if (peptideCount > 0) {
            const hasValidReceptor = window.currentPdbID && window.currentReceptorID && window.currentPdbID !== '1tqn';
            const hasBindingSite = window.currentBindingSiteID;
            const hasCustomBindingSite = window.currentCustomBindingSiteID;
            const bindingSiteDisplay = hasCustomBindingSite
                ? `custom binding site <b>${hasCustomBindingSite}</b>`
                : hasBindingSite
                    ? `binding site <b>${window.currentBindingSiteID}</b>`
                    : null;
            if (hasValidReceptor && (hasBindingSite || hasCustomBindingSite)) {
                // All conditions met - show docking message (receptor and binding site in bold)
                peptideDetails.innerHTML = `🔍 Docking with receptor <b>${window.currentPdbID}</b> and ${bindingSiteDisplay}`;
                peptideDetails.style.display = 'block';
            }
            else {
                // Show what's missing
                let missingItems = [];
                if (!hasValidReceptor) {
                    missingItems.push('receptor');
                }
                if (!hasBindingSite && !hasCustomBindingSite) {
                    missingItems.push('binding site');
                }
                if (missingItems.length === 2) {
                    peptideDetails.innerHTML = `<span style="color: #3b82f6; font-weight: 600;">ℹ️</span> Select receptor and binding site to enable docking`;
                }
                else if (missingItems.includes('receptor')) {
                    peptideDetails.innerHTML = `<span style="color: #3b82f6; font-weight: 600;">ℹ️</span> Select receptor to enable docking`;
                }
                else if (missingItems.includes('binding site')) {
                    peptideDetails.innerHTML = `<span style="color: #3b82f6; font-weight: 600;">ℹ️</span> Select binding site to enable docking`;
                }
                peptideDetails.style.display = 'block';
            }
            // Update docking button states based on receptor/binding site availability
            updateDockingButtonStates(Boolean(hasValidReceptor && (hasBindingSite || hasCustomBindingSite)));
        }
        else {
            peptideDetails.style.display = 'none';
        }
    }
    // === Update docking button states ===
    function updateDockingButtonStates(dockingEnabled) {
        const allDockButtons = peptideList.querySelectorAll('.btn-dock');
        allDockButtons.forEach(btn => {
            var _a;
            const button = btn;
            const peptideItem = peptides.get(parseInt(((_a = button.closest('.peptide-item')) === null || _a === void 0 ? void 0 : _a.getAttribute('data-local-id')) || '0'));
            if (peptideItem) {
                // Button is enabled only if peptide is ready AND docking is enabled
                button.disabled = !(peptideItem.peptideGenerationOK && dockingEnabled);
                button.title = peptideItem.peptideGenerationOK && dockingEnabled
                    ? 'Launch docking'
                    : !peptideItem.peptideGenerationOK
                        ? 'Peptide must be ready first'
                        : 'Select receptor and submit binding site to enable docking';
            }
        });
    }
    window.selectPeptideById = (peptideID) => {
        const peptideItem = Array.from(globalPeptides.values()).find(p => p.peptideID === peptideID);
        if (!(peptideItem === null || peptideItem === void 0 ? void 0 : peptideItem.element))
            return false;
        peptideItem.element.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        selectPeptide(peptideItem);
        return true;
    };
    // Chat agent: add peptide row if missing, then refresh status (same as manual Launch)
    window.syncPeptideFromAgent = (peptideID) => __awaiter(this, void 0, void 0, function* () {
        var _a, _b;
        const requestedSessionId = resolveCurrentSessionId();
        const loadGen = getSessionLoadGeneration();
        let peptideItem = Array.from(globalPeptides.values()).find(p => p.peptideID === peptideID);
        if (!peptideItem) {
            const sessionData = yield getSessionPeptides();
            if (!isSessionLoadCurrent(loadGen, requestedSessionId))
                return false;
            const p = (_a = sessionData.peptides) === null || _a === void 0 ? void 0 : _a.find(x => x.peptideID === peptideID);
            if (!p) {
                console.warn('[syncPeptideFromAgent] Peptide not found in session:', peptideID);
                return false;
            }
            // Same sequence already shown (race / pre-dedupe) — refresh that row instead of appending.
            const seqKey = (p.peptideSequence || '').replace(/-/g, ' ').replace(/\s+/g, ' ').trim();
            const existingBySeq = Array.from(globalPeptides.values()).find((item) => {
                const key = (item.peptideSequence || '').replace(/-/g, ' ').replace(/\s+/g, ' ').trim();
                return key === seqKey;
            });
            if (existingBySeq) {
                yield refreshPeptide(existingBySeq);
                if (!isSessionLoadCurrent(loadGen, requestedSessionId))
                    return false;
                if (existingBySeq.element) {
                    existingBySeq.element.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                    selectPeptide(existingBySeq);
                }
                return true;
            }
            if (!isSessionLoadCurrent(loadGen, requestedSessionId))
                return false;
            peptideItem = {
                localID: nextLocalId++,
                peptideID: p.peptideID,
                peptideSequence: p.peptideSequence,
                peptideGenerationOK: p.peptideGenerationOK || false,
                status: p.status || 'pending',
                statusMessage: p.statusMessage || '',
                peptideQED: p.peptideQED,
                peptideALOGP: p.peptideALOGP,
                peptideMW: p.peptideMW,
                peptideHBD: p.peptideHBD,
                peptideHBA: p.peptideHBA,
                peptidePSA: p.peptidePSA,
                peptideROTB: p.peptideROTB,
                peptideAROM: p.peptideAROM,
            };
            const element = createPeptideElement(peptideItem);
            peptideItem.element = element;
            peptides.set(peptideItem.localID, peptideItem);
            prepareWorkflowListForItems(peptideList);
            peptideList.appendChild(element);
            peptideCount++;
            const numberSpan = element.querySelector('.peptide-number');
            if (numberSpan)
                numberSpan.textContent = `${peptideCount}`;
            updatePeptideDetails();
            updatePeptideDeleteButtons();
            updatePeptideInfoButtons();
            element.setAttribute('data-just-launched', 'true');
        }
        yield refreshPeptide(peptideItem);
        if (!isSessionLoadCurrent(loadGen, requestedSessionId))
            return false;
        (_b = peptideItem.element) === null || _b === void 0 ? void 0 : _b.removeAttribute('data-just-launched');
        // Same as a manual click: select + scroll the refreshed row.
        if (peptideItem.element) {
            peptideItem.element.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
            selectPeptide(peptideItem);
        }
        return true;
    });
}
/**
 * Updates the peptide details text from outside components
 */
export function updatePeptideDetailsGlobal() {
    if (globalUpdatePeptideDetails) {
        globalUpdatePeptideDetails();
    }
}
// Process peptide deletion queue sequentially
function processPeptideDeletionQueue() {
    return __awaiter(this, void 0, void 0, function* () {
        if (isDeletingPeptide || peptideDeletionQueue.length === 0) {
            return;
        }
        isDeletingPeptide = true;
        if (globalUpdatePeptideDeleteButtons) {
            globalUpdatePeptideDeleteButtons();
        }
        while (peptideDeletionQueue.length > 0) {
            const localID = peptideDeletionQueue.shift();
            try {
                // Get peptide item from the global peptides map
                const peptideItem = Array.from(globalPeptides.values()).find(p => p.localID === localID);
                if (!peptideItem)
                    continue;
                // Delete from backend (if peptideID available)
                if (peptideItem.peptideID) {
                    const res = yield deleteSessionPeptide(peptideItem.peptideID);
                    if (!res.deleted) {
                        console.error(`Failed to delete peptide ${peptideItem.peptideID} from backend`, res);
                        if (res.reason === 'blocked_by_docking') {
                            alert('Cannot delete this peptide because a docking references it. Remove related dockings first.');
                        }
                        else {
                            alert(res.message || 'Failed to delete peptide from database. Please try again.');
                        }
                        continue; // Skip to next item in queue
                    }
                }
                // Remove from UI immediately after successful backend deletion
                if (peptideItem.element)
                    peptideItem.element.remove();
                const deletedBackendId = peptideItem.peptideID;
                globalPeptides.delete(localID);
                // Clear global selection if this was the selected peptide
                if (typeof deletedBackendId === 'number' && window.currentPeptideID === deletedBackendId) {
                    window.currentPeptideID = undefined;
                    // Remove peptide from Mol* visualizer
                    if (window.removeFromMolstar) {
                        window.removeFromMolstar(`peptide-${deletedBackendId}`);
                    }
                }
                // Update UI state
                const peptideList = document.getElementById('peptide-list-container');
                if (peptideList) {
                    // Update numbering based on chronological order (1, 2, 3...)
                    const peptideItems = peptideList.querySelectorAll('.peptide-item');
                    peptideItems.forEach((item, index) => {
                        const numberSpan = item.querySelector('.peptide-number');
                        if (numberSpan)
                            numberSpan.textContent = `${index + 1}`;
                    });
                }
                // Update peptide count
                const peptideCount = globalPeptides.size;
                if (globalUpdatePeptideDetails) {
                    globalUpdatePeptideDetails();
                }
            }
            catch (error) {
                console.error('Error deleting peptide from session:', error);
                alert('Error deleting peptide. Please try again.');
            }
        }
        // Reset deletion state and update delete buttons
        isDeletingPeptide = false;
        if (globalUpdatePeptideDeleteButtons) {
            globalUpdatePeptideDeleteButtons();
        }
    });
}
// Export loadSessionPeptides function
export function loadSessionPeptides() {
    if (globalLoadSessionPeptides) {
        return globalLoadSessionPeptides();
    }
    else {
        console.error('loadSessionPeptides not initialized');
        return Promise.resolve();
    }
}
// ===========================
// PEPTIDE INFO MODAL FUNCTIONS
// ===========================
function showPeptideInfoModal(peptideItem) {
    return __awaiter(this, void 0, void 0, function* () {
        console.log('📊 [DEBUG] Opening peptide info modal for:', peptideItem);
        const modal = document.getElementById('info-modal');
        const title = document.getElementById('info-modal-title');
        const body = document.getElementById('info-modal-body');
        if (!modal || !title || !body) {
            console.error('❌ [ERROR] Modal elements not found');
            return;
        }
        // Set title
        title.textContent = 'Peptide Properties';
        // Get current user ID from localStorage or session
        const currentUser = JSON.parse(localStorage.getItem('currentUser') || '{}');
        const userId = currentUser.username || 'unknown';
        const currentDate = new Date().toISOString().split('T')[0]; // YYYY-MM-DD format
        // Use peptide data from the item itself (already contains all properties)
        const peptideData = peptideItem;
        console.log('📊 [DEBUG] Using peptide data from item:', peptideData);
        console.log('🔍 [DEBUG] Available keys in peptideData:', Object.keys(peptideData));
        // Debug specific properties
        console.log('🔍 [DEBUG] peptideQED:', peptideData.peptideQED);
        console.log('🔍 [DEBUG] peptideMW:', peptideData.peptideMW);
        console.log('🔍 [DEBUG] peptideALOGP:', peptideData.peptideALOGP);
        console.log('🔍 [DEBUG] peptidePSA:', peptideData.peptidePSA);
        // Create table content with sequence first, then properties in alphabetical order
        const tableContent = `
    <table class="info-table">
      <thead>
        <tr>
          <th>Property</th>
          <th>Value</th>
          <th>Description</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td><strong>Sequence</strong></td>
          <td style="font-family: 'Courier New', monospace; white-space: nowrap; min-width: 200px;">${peptideItem.peptideSequence}</td>
          <td>Peptide amino acid sequence</td>
        </tr>
        <tr>
          <td><strong>ALOGP</strong></td>
          <td>${getPropertyValue('ALOGP', peptideData)}</td>
          <td>Lipophilicity (log of partition coefficient)</td>
        </tr>
        <tr>
          <td><strong>AROM</strong></td>
          <td>${getPropertyValue('AROM', peptideData)}</td>
          <td>Aromatic rings count</td>
        </tr>
        <tr>
          <td><strong>HBA</strong></td>
          <td>${getPropertyValue('HBA', peptideData)}</td>
          <td>Hydrogen bond acceptors count</td>
        </tr>
        <tr>
          <td><strong>HBD</strong></td>
          <td>${getPropertyValue('HBD', peptideData)}</td>
          <td>Hydrogen bond donors count</td>
        </tr>
        <tr>
          <td><strong>MW</strong></td>
          <td>${getPropertyValue('MW', peptideData)} Da</td>
          <td>Molecular weight in Daltons</td>
        </tr>
        <tr>
          <td><strong>PSA</strong></td>
          <td>${getPropertyValue('PSA', peptideData)} Ų</td>
          <td>Polar surface area in Ų</td>
        </tr>
        <tr>
          <td><strong>QED</strong></td>
          <td>${getPropertyValue('QED', peptideData)}</td>
          <td>Quantitative Estimate of Drug-likeness (0-1, higher is better)</td>
        </tr>
        <tr>
          <td><strong>ROTB</strong></td>
          <td>${getPropertyValue('ROTB', peptideData)}</td>
          <td>Rotatable bonds count</td>
        </tr>
      </tbody>
    </table>
  `;
        body.innerHTML = tableContent;
        // Calculate modal width based on peptide sequence length
        const sequenceLength = peptideItem.peptideSequence.length;
        const baseWidth = 1200; // Base width for short sequences
        const charWidth = 15; // Approximate character width in pixels for monospace font
        const padding = 600; // Extra padding for table structure and description column
        const calculatedWidth = Math.max(baseWidth, sequenceLength * charWidth + padding);
        const maxWidth = Math.min(calculatedWidth, window.innerWidth * 0.85); // Max 85% of screen width
        // Apply dynamic width
        const modalContent = modal.querySelector('.modal-content');
        if (modalContent) {
            modalContent.style.width = `${maxWidth}px`;
            modalContent.style.maxWidth = 'none';
        }
        // Show modal
        modal.classList.remove('hidden');
        // Setup export button in header
        const exportBtn = document.getElementById('info-modal-export-header');
        if (exportBtn) {
            exportBtn.style.display = 'flex';
            exportBtn.onclick = () => {
                var _a;
                window.exportPeptideCSV(((_a = peptideItem.peptideID) === null || _a === void 0 ? void 0 : _a.toString()) || '', userId, currentDate, peptideItem.localID.toString());
            };
        }
        // Hide export icon button for peptide properties
        const exportIconBtn = document.getElementById('info-modal-export-icon');
        if (exportIconBtn) {
            exportIconBtn.style.display = 'none';
        }
        // Setup close handlers
        setupModalCloseHandlers();
    });
}
function getPropertyValue(property, peptideData) {
    if (!peptideData)
        return 'N/A';
    console.log('🔍 [DEBUG] Looking for property:', property, 'in data:', peptideData);
    // Map property names to the actual keys in peptideData
    const propertyMap = {
        'ALOGP': 'peptideALOGP',
        'AROM': 'peptideAROM',
        'HBA': 'peptideHBA',
        'HBD': 'peptideHBD',
        'MW': 'peptideMW',
        'PSA': 'peptidePSA',
        'QED': 'peptideQED',
        'ROTB': 'peptideROTB'
    };
    const key = propertyMap[property];
    if (key && peptideData[key] !== undefined && peptideData[key] !== null) {
        const value = peptideData[key];
        const cleanValue = typeof value === 'number' ? value.toFixed(2) : value.toString().trim();
        console.log('✅ [DEBUG] Found property:', key, '=', cleanValue);
        return cleanValue;
    }
    console.log('❌ [DEBUG] Property not found:', property, 'mapped to:', key);
    return 'N/A';
}
function setupModalCloseHandlers() {
    const modal = document.getElementById('info-modal');
    const closeBtn = document.getElementById('info-modal-close');
    if (!modal || !closeBtn)
        return;
    // Close on button click
    closeBtn.onclick = () => modal.classList.add('hidden');
    // Close on overlay click
    modal.onclick = (e) => {
        if (e.target === modal) {
            modal.classList.add('hidden');
        }
    };
    // Close on Escape key
    const handleEscape = (e) => {
        if (e.key === 'Escape') {
            modal.classList.add('hidden');
            document.removeEventListener('keydown', handleEscape);
        }
    };
    document.addEventListener('keydown', handleEscape);
}
// Global function for CSV export (accessible from HTML onclick)
window.exportPeptideCSV = function (peptideID, userId, date, localID) {
    console.log('📄 [DEBUG] Exporting peptide CSV for:', { peptideID, userId, date, localID });
    // Find the peptide item to get all properties
    const peptideList = Array.from(document.querySelectorAll('.peptide-item'));
    const targetElement = peptideList.find(el => el.getAttribute('data-local-id') === localID);
    if (!targetElement) {
        console.error('❌ [ERROR] Peptide item not found for CSV export');
        return;
    }
    // Get the peptide data from the global peptides map
    const peptideData = globalPeptides.get(parseInt(localID));
    if (!peptideData) {
        console.error('❌ [ERROR] Peptide data not found for CSV export');
        return;
    }
    // Create CSV content matching the table exactly
    const rows = [
        ['Property', 'Value', 'Description'],
        ['Sequence', peptideData.peptideSequence, 'Peptide amino acid sequence'],
        ['ALOGP', getPropertyValue('ALOGP', peptideData), 'Lipophilicity (log of partition coefficient)'],
        ['AROM', getPropertyValue('AROM', peptideData), 'Aromatic rings count'],
        ['HBA', getPropertyValue('HBA', peptideData), 'Hydrogen bond acceptors count'],
        ['HBD', getPropertyValue('HBD', peptideData), 'Hydrogen bond donors count'],
        ['MW', getPropertyValue('MW', peptideData) + ' Da', 'Molecular weight in Daltons'],
        ['PSA', getPropertyValue('PSA', peptideData) + ' Ų', 'Polar surface area in Ų'],
        ['QED', getPropertyValue('QED', peptideData), 'Quantitative Estimate of Drug-likeness (0-1, higher is better)'],
        ['ROTB', getPropertyValue('ROTB', peptideData), 'Rotatable bonds count']
    ];
    const csvContent = rows.map(row => row.map(field => `"${field}"`).join(',')).join('\n');
    // Create and download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const filename = `peptide_${peptideID}_${userId}_${date}.csv`;
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    console.log('✅ [SUCCESS] CSV exported:', filename);
};
// No list-height JS: pure CSS
